import React, { useCallback, useEffect, useState } from 'react';
import { money } from '../../../lib/money';
import { useNavigate } from 'react-router-dom';
import { Badge, DataTable, EmptyState, Spinner, useToast } from '../../../ui/components';
import { Icon } from '../../../ui/icons';
import { useT } from '../../../i18n';
import { purchaseOrderPath } from '../../../lib/slug';
import { purchaseOrdersApi, poStatusMeta } from '../../purchase-orders/purchaseOrdersApi';

const date = (d) => (d ? new Date(d).toLocaleDateString() : '—');

export default function SupplierPurchasesTab({ supplierId }) {
  const toast = useToast();
  const navigate = useNavigate();
  const t = useT();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await purchaseOrdersApi.list({ supplierId, pageSize: 100, sortBy: 'createdat', descending: true });
      setOrders(res?.items || []);
    } catch (err) {
      toast.error(t('Could not load purchase orders'), { description: err?.message });
    } finally {
      setLoading(false);
    }
  }, [supplierId, toast, t]);

  useEffect(() => { load(); }, [load]);

  const columns = [
    { key: 'number', header: t('PO #'), width: 140, render: (r) => <span style={{ fontWeight: 600 }}>{r.number}</span> },
    { key: 'title', header: t('Title'), width: 260, render: (r) => r.title || <span className="pp-cell-muted">—</span> },
    {
      key: 'status', header: t('Status'), width: 150,
      render: (r) => { const m = poStatusMeta(r.status); return <Badge tone={m.tone} dot>{t(m.label)}</Badge>; },
    },
    { key: 'expected', header: t('Expected'), width: 130, render: (r) => date(r.expectedDate) },
    { key: 'total', header: t('Total'), width: 140, align: 'right', render: (r) => money(r.total) },
    { key: 'created', header: t('Created'), width: 130, render: (r) => date(r.createdAt) },
  ];

  if (loading) {
    return <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-12)' }}><Spinner size={24} /></div>;
  }

  if (orders.length === 0) {
    return (
      <EmptyState
        icon={<Icon.ShoppingCart size={28} />}
        title={t('No purchase orders yet')}
        description={t('Purchase orders issued to this supplier will appear here.')}
      />
    );
  }

  return (
    <DataTable
      columns={columns}
      data={orders}
      rowKey={(r) => r.id}
      density="comfortable"
      onRowClick={(r) => navigate(purchaseOrderPath(r))}
    />
  );
}
